import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeCheckbox, TeContainer, TeContainerSlim, TeText } from '@topcon/tuicl-react';
import styled from 'styled-components';

import { ServiceAgreementTermsEnum } from '../../api/generated/pm';

export type ServiceAgreementSide = 'dealer' | 'customer';

export type ServiceAgreementAcknowledgementKey =
  | 'attestationDealer'
  | 'attestationCustomerPermissions'
  | 'attestationCustomerManagement'
  | 'attestationBothParties';

export function getRequiredAcknowledgementKeys(
  side: ServiceAgreementSide,
  tier: ServiceAgreementTermsEnum | undefined
): ServiceAgreementAcknowledgementKey[] {
  if (!tier) {
    return [];
  }
  if (side === 'dealer') {
    if (tier === ServiceAgreementTermsEnum.PARTIAL_SERVICE) {
      return ['attestationDealer'];
    }
    if (tier === ServiceAgreementTermsEnum.FULL_SERVICE) {
      return ['attestationDealer', 'attestationBothParties'];
    }
  }
  if (side === 'customer') {
    if (tier === ServiceAgreementTermsEnum.PARTIAL_SERVICE) {
      return ['attestationCustomerPermissions'];
    }
    if (tier === ServiceAgreementTermsEnum.FULL_SERVICE) {
      return [
        'attestationCustomerPermissions',
        'attestationCustomerManagement',
        'attestationBothParties',
      ];
    }
  }
  return [];
}

export function getAcknowledgementTranslationKey(key: ServiceAgreementAcknowledgementKey): string {
  return `serviceAgreement.agreementReview.${key}`;
}

export type ServiceAgreementAcknowledgementsVariant = 'modal' | 'review';

export interface ServiceAgreementAcknowledgementsProps {
  side: ServiceAgreementSide;
  tier: ServiceAgreementTermsEnum | undefined;
  onAcknowledged: (allAcknowledged: boolean) => void;
  variant?: ServiceAgreementAcknowledgementsVariant;
}

function ServiceAgreementAcknowledgements({
  side,
  tier,
  onAcknowledged,
  variant = 'review',
}: ServiceAgreementAcknowledgementsProps) {
  const { t } = useTranslation();
  const requiredKeys = useMemo(() => getRequiredAcknowledgementKeys(side, tier), [side, tier]);

  const [prevTier, setPrevTier] = useState(tier);
  const [prevSide, setPrevSide] = useState(side);
  const [checked, setChecked] = useState<Set<ServiceAgreementAcknowledgementKey>>(() => new Set());

  if (tier !== prevTier || side !== prevSide) {
    setPrevTier(tier);
    setPrevSide(side);
    setChecked(new Set());
  }

  useEffect(() => {
    const isComplete = requiredKeys.length > 0 && requiredKeys.every((key) => checked.has(key));
    onAcknowledged(isComplete);
  }, [requiredKeys, checked, onAcknowledged]);

  if (requiredKeys.length === 0) {
    return null;
  }

  const toggleKey = (key: ServiceAgreementAcknowledgementKey) => {
    setChecked((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const rows = requiredKeys.map((key) => {
    const isChecked = checked.has(key);
    return (
      <AcknowledgementRow fill gap="xs" key={key} alignItems="flex-start">
        <TeCheckbox
          checked={isChecked}
          style={{ alignSelf: 'flex-start', flexShrink: 0 }}
          techange={() => toggleKey(key)}
        />
        <AcknowledgementTextWrap grow fill column padding={0} onClick={() => toggleKey(key)}>
          <AcknowledgementText size={14} weight={400}>
            {t(getAcknowledgementTranslationKey(key))}
          </AcknowledgementText>
        </AcknowledgementTextWrap>
      </AcknowledgementRow>
    );
  });

  if (variant === 'modal') {
    return (
      <ModalShell
        column
        gap="xs"
        padding="m"
        fill
        backgroundColor="background.primary"
        borderColor="border.secondary"
        borderRadius="m"
        justifyContent="flex-start"
      >
        {rows}
      </ModalShell>
    );
  }

  return (
    <ReviewShell column fill gap="s" padding={['m', 'xl']} grow>
      {rows}
    </ReviewShell>
  );
}

const AcknowledgementRow = styled(TeContainerSlim)`
  min-width: 0;
  width: 100%;
`;

const AcknowledgementTextWrap = styled(TeContainerSlim)`
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  cursor: pointer;
  user-select: none;
`;

const AcknowledgementText = styled(TeText)`
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  max-width: 100%;
`;

const ModalShell = styled(TeContainer)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const ReviewShell = styled(TeContainerSlim)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export default ServiceAgreementAcknowledgements;
